import { useState, useEffect, useCallback } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { Child } from '@/types/child';
import { useToast } from './use-toast';
import { useChildOperations } from './useChildOperations';
import { useFamilyPermissions } from './useFamilyPermissions';
import { logger } from '@/utils/logger';

export function useChildren(userId: string | undefined) {
  const [children, setChildren] = useState<Child[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const refreshData = useCallback(async () => {
    if (!userId) {
      setChildren([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('children')
        .select('id, name, birthdate, photo_url')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching children:', error);
        setError(error.message);
        toast({
          title: "Error",
          description: "Failed to load child profiles. Please try again.",
          variant: "destructive"
        });
      } else {
        const formattedChildren: Child[] = data.map(child => ({
          id: child.id,
          name: child.name,
          birthdate: child.birthdate,
          photoUrl: child.photo_url
        }));
        setChildren(formattedChildren);
      }
    } catch (err: any) {
      logger.error('Unexpected error fetching children:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    refreshData();
  }, [userId, refreshData]);

  const { familyId } = useFamilyPermissions();
  const childOps = useChildOperations(userId, familyId, refreshData);
  
  const addChild = async (childData: { 
    name: string; 
    birthdate: string;
    photo?: File;
  }) => {
    return childOps.addChild(childData);
  };

  return {
    children,
    isLoading,
    error,
    addChild,
    refreshData
  };
}
